import React, { useState, useEffect } from 'react';
import { Box, Container, ThemeProvider, createTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Components
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import TournamentDialog from './components/TournamentDialog';
import TournamentsView from './components/TournamentsView';
import GetStarted from './components/GetStarted';
import CsvImport from './components/CsvImport';
import WrestlersView from './components/WrestlersView';
import TeamsOverview from './components/TeamsOverview';
import DrawView from './components/DrawView';

// Auth
import AuthProvider from './contexts/AuthContext';

// Hooks
import { useTournament } from './hooks/useTournament';
import { useCsvImport, Wrestler } from './hooks/useCsvImport';
import { useTournamentExport } from './hooks/useTournamentExport';

// Local storage helper functions
const saveAppStateToLocalStorage = (state: any) => {
  try {
    localStorage.setItem('appState', JSON.stringify(state));
  } catch (error) {
    console.error('Error saving app state to localStorage:', error);
  }
};

const loadAppStateFromLocalStorage = () => {
  try {
    const savedState = localStorage.getItem('appState');
    return savedState ? JSON.parse(savedState) : null;
  } catch (error) {
    console.error('Error loading app state from localStorage:', error);
    return null;
  }
};

export interface WrestlerWithSchool extends Wrestler {
  schoolName: string;
}

const App: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentView, setCurrentView] = useState<'import' | 'wrestlers' | 'teams' | 'draw' | 'tournaments'>('import');
  const [allSubmittedWrestlers, setAllSubmittedWrestlers] = useState<WrestlerWithSchool[]>([]);
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>('light');
  const [currentDrawState, setCurrentDrawState] = useState<any>(null);
  const [currentWeightGroups, setCurrentWeightGroups] = useState<any>(null);

  // Load saved state from local storage on mount
  useEffect(() => {
    const savedState = loadAppStateFromLocalStorage();
    if (savedState) {
      setAllSubmittedWrestlers(savedState.allSubmittedWrestlers || []);
      setCurrentView(savedState.currentView || 'import');
      setCurrentDrawState(savedState.currentDrawState || null);
      setCurrentWeightGroups(savedState.currentWeightGroups || null);
      setThemeMode(savedState.themeMode || 'light');
    }
  }, []);

  // Save state to local storage when wrestlers or important state changes
  useEffect(() => {
    if (allSubmittedWrestlers.length > 0) {
      saveAppStateToLocalStorage({
        allSubmittedWrestlers,
        currentView,
        currentDrawState,
        currentWeightGroups,
        themeMode
      });
    }
  }, [allSubmittedWrestlers, currentView, currentDrawState, currentWeightGroups, themeMode]);

  const handleThemeToggle = () => {
    setThemeMode(prev => prev === 'light' ? 'dark' : 'light');
  };

  const theme = createTheme({
    palette: {
      mode: themeMode,
      primary: {
        main: '#89479a',
      },
      secondary: {
        main: '#f78f29',
      },
      background: {
        default: themeMode === 'light' ? '#F4F6F8' : '#0F1216',
        paper: themeMode === 'light' ? '#FFFFFF' : '#171B21',
      },
    },
  });

  // Tournament hook
  const tournamentHook = useTournament();

  // Tournament export/import hook
  const tournamentExportHook = useTournamentExport();

  // CSV import hook
  const csvImportHook = useCsvImport(
    allSubmittedWrestlers,
    (wrestlers, schoolName) => {
      const wrestlersWithSchool = wrestlers.map(w => ({ ...w, schoolName }));
      setAllSubmittedWrestlers(prev => [...prev, ...wrestlersWithSchool]);
    }
  );

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMenuItemClick = (item: string) => {
    setDrawerOpen(false);
    if (item === 'tournaments') {
      setCurrentView('tournaments');
    } else if (item === 'import') {
      setCurrentView('import');
      csvImportHook.handleReset();
    } else if (item === 'wrestlers') {
      setCurrentView('wrestlers');
    } else if (item === 'teams') {
      setCurrentView('teams');
    } else if (item === 'draw') {
      setCurrentView('draw');
    }
  };

  const handleSelectTournament = (tournament: { name: string; date: string; id: string }) => {
    tournamentHook.handleSelectTournament(tournament);
    setAllSubmittedWrestlers([]);
    setCurrentView('import');
    csvImportHook.handleReset();
    csvImportHook.setSuccess(`Switched to tournament: ${tournament.name}`);
  };

  const handleRemoveSchool = (schoolName: string) => {
    setAllSubmittedWrestlers(prev => prev.filter(w => w.schoolName !== schoolName));
  };

  const handleExportTournament = () => {
    if (!tournamentHook.currentTournament) {
      return;
    }

    console.log('Export Debug: currentDrawState', currentDrawState);
    console.log('Export Debug: currentDrawState.adjustedWeightClasses', currentDrawState?.adjustedWeightClasses);

    // Get current weight groups from DrawView if available
    const getWeightGroupsFromDrawView = () => {
      // This would need to be passed from DrawView or accessed via refs
      // For now, we'll create basic weight groups from wrestlers
      const weightGroups: Array<{ name: string; wrestlers: string[]; minWeight: number; maxWeight: number; gender?: string }> = [];
      const maleWrestlers = allSubmittedWrestlers.filter(w => w.gender === 'male' || !w.gender);
      const femaleWrestlers = allSubmittedWrestlers.filter(w => w.gender === 'female');
      
      // Create weight classes (this is basic logic - could be enhanced)
      const maleWeights = maleWrestlers.map(w => w.weightKg).sort((a, b) => a - b);
      const femaleWeights = femaleWrestlers.map(w => w.weightKg).sort((a, b) => a - b);
      
      // Basic weight class creation (every 10kg range)
      const createWeightClasses = (weights: number[], wrestlers: any[], prefix: string) => {
        const classes = [];
        for (let i = 0; i < weights.length; i += 3) {
          const classWeights = weights.slice(i, i + 3);
          if (classWeights.length > 0) {
            const min = Math.floor(Math.min(...classWeights));
            const max = Math.ceil(Math.max(...classWeights));
            const classWrestlers = wrestlers.filter(w => 
              w.weightKg >= min && w.weightKg <= max
            );
            classes.push({
              name: `${prefix}${min}-${max}kg`,
              wrestlers: classWrestlers.map(w => w.id || `${w.firstName}_${w.lastName}`),
              minWeight: min,
              maxWeight: max,
              gender: prefix === 'Boys ' ? 'male' : 'female'
            });
          }
        }
        return classes;
      };
      
      weightGroups.push(...createWeightClasses(maleWeights, maleWrestlers, 'Boys '));
      weightGroups.push(...createWeightClasses(femaleWeights, femaleWrestlers, 'Girls '));
      
      return weightGroups;
    };

    const exportData = {
      version: '1.0',
      tournament: {
        id: tournamentHook.currentTournament.id,
        name: tournamentHook.currentTournament.name,
        date: tournamentHook.currentTournament.date,
        createdAt: new Date().toISOString(),
      },
      wrestlers: allSubmittedWrestlers.map(w => ({
        id: w.id,
        firstName: w.firstName,
        lastName: w.lastName,
        weightKg: w.weightKg,
        age: w.age,
        grade: w.grade,
        gender: w.gender,
        schoolName: w.schoolName,
      })),
      weightGroups: getWeightGroupsFromDrawView(),
      drawState: currentDrawState ? {
        weightDelta: currentDrawState.weightDelta,
        separateGenders: currentDrawState.separateGenders,
        adjustedWeightClasses: currentDrawState.adjustedWeightClasses?.map((cls: any) => ({
          name: cls.name,
          wrestlers: cls.wrestlers.map((w: any) => {
            // Convert wrestler objects to IDs for export
            if (typeof w === 'string') return w;
            if (w && typeof w === 'object' && w.firstName && w.lastName) {
              return w.firstName + '_' + w.lastName;
            }
            return w.id || 'unknown';
          }),
          minWeight: cls.minWeight,
          maxWeight: cls.maxWeight,
          gender: cls.gender
        })) || [],
        scheduledMatches: currentDrawState.scheduledMatches?.map((match: any) => ({
          boutNumber: match.boutNumber,
          wrestler1Id: match.wrestler1Id,
          wrestler2Id: match.wrestler2Id,
          mat: match.mat,
          weightClass: match.weightClass
        })) || [],
        numMats: currentDrawState.numMats
      } : undefined
    };

    console.log('Export Debug: Final export data', {
      hasDrawState: !!exportData.drawState,
      adjustedWeightClassesCount: exportData.drawState?.adjustedWeightClasses?.length || 0,
      adjustedWeightClasses: exportData.drawState?.adjustedWeightClasses
    });

    tournamentExportHook.exportTournament(exportData);
  };

  const handleImportTournament = async (file: File) => {
    const data = await tournamentExportHook.importTournament(file);
    if (data) {
      console.log('Import Debug: Imported data', {
        hasDrawState: !!data.drawState,
        adjustedWeightClassesCount: data.drawState?.adjustedWeightClasses?.length || 0,
        drawState: data.drawState
      });

      // Add tournament if it doesn't exist
      const existingTournament = tournamentHook.tournaments.find(t => t.id === data.tournament.id);
      if (!existingTournament) {
        const newTournaments = [...tournamentHook.tournaments, {
          id: data.tournament.id,
          name: data.tournament.name,
          date: data.tournament.date,
        }];
        tournamentHook.setTournaments(newTournaments);
      }

      // Add wrestlers
      const wrestlersWithSchool = data.wrestlers.map(w => ({ ...w, schoolName: w.schoolName }));
      setAllSubmittedWrestlers(prev => [...prev, ...wrestlersWithSchool]);

      // Restore draw state if it exists
      if (data.drawState) {
        // If there are adjusted weight classes, go to step 2 (Adjust Groupings)
        const hasAdjustedWeightClasses = data.drawState.adjustedWeightClasses && data.drawState.adjustedWeightClasses.length > 0;
        
        const restoredDrawState = {
          currentStep: hasAdjustedWeightClasses ? 1 : 0, // Step 2 is index 1, Step 1 is index 0
          drawComplete: false, // Default since not in interface
          weightGroups: data.weightGroups || [],
          weightDelta: data.drawState.weightDelta,
          separateGenders: data.drawState.separateGenders,
          adjustedWeightClasses: data.drawState.adjustedWeightClasses,
          activeTab: 0, // Default since not in interface
          numMats: data.drawState.numMats || 2,
          scheduledMatches: data.drawState.scheduledMatches || [],
          viewMode: 'schedule' as const // Default since not in interface
        };
        
        console.log('Import Debug: Setting draw state', {
          ...restoredDrawState,
          hasAdjustedWeightClasses,
          navigatingToStep: hasAdjustedWeightClasses ? 'Step 2 (Adjust Groupings)' : 'Step 1 (Initial Setup)'
        });
        setCurrentDrawState(restoredDrawState);
      }

      // Switch to the imported tournament
      tournamentHook.handleSelectTournament({
        id: data.tournament.id,
        name: data.tournament.name,
        date: data.tournament.date,
      });
      setCurrentView('import');
      csvImportHook.handleReset();
      csvImportHook.setSuccess(`Imported tournament: ${data.tournament.name} with ${data.wrestlers.length} wrestlers${data.weightGroups ? ' and ' + data.weightGroups.length + ' weight groups' : ''}${data.drawState?.adjustedWeightClasses ? ' and ' + data.drawState.adjustedWeightClasses.length + ' adjusted weight classes' : ''}`);
    }
  };

  const renderMainContent = () => {
    if (currentView === 'import') {
      if (!tournamentHook.currentTournament) {
        return (
          <>
            <GetStarted onCreateTournament={tournamentHook.handleCreateTournament} />
            <TournamentDialog
              open={tournamentHook.tournamentDialogOpen}
              onClose={tournamentHook.handleCreateTournamentCancel}
              onConfirm={tournamentHook.handleCreateTournamentConfirm}
              tournamentName={tournamentHook.newTournamentName}
              onTournamentNameChange={tournamentHook.setNewTournamentName}
              tournamentDate={tournamentHook.newTournamentDate}
              onTournamentDateChange={tournamentHook.setNewTournamentDate}
            />
          </>
        );
      }
      return (
        <CsvImport
          wrestlers={csvImportHook.wrestlers}
          schoolName={csvImportHook.schoolName}
          weightUnit={csvImportHook.weightUnit}
          loading={csvImportHook.loading}
          error={csvImportHook.error}
          success={csvImportHook.success}
          submitted={csvImportHook.submitted}
          submitting={csvImportHook.submitting}
          allSubmittedWrestlers={allSubmittedWrestlers}
          rawWeights={csvImportHook.rawWeights}
          csvData={csvImportHook.csvData}
          detectedColumns={csvImportHook.detectedColumns}
          columnMappings={csvImportHook.columnMappings}
          showColumnMapping={csvImportHook.showColumnMapping}
          onFileUpload={csvImportHook.handleFileUpload}
          onPasteData={csvImportHook.handlePasteData}
          onSchoolNameChange={csvImportHook.setSchoolName}
          onWeightUnitChange={csvImportHook.handleWeightUnitChange}
          onSubmit={csvImportHook.handleSubmit}
          onReset={csvImportHook.handleReset}
          onColumnMappingChange={csvImportHook.handleColumnMappingChange}
          onColumnMappingConfirm={csvImportHook.handleColumnMappingConfirm}
          onColumnMappingCancel={csvImportHook.handleColumnMappingCancel}
        />
      );
    }

    if (currentView === 'tournaments') {
      return (
        <>
        <TournamentsView
          tournaments={tournamentHook.tournaments}
          currentTournament={tournamentHook.currentTournament}
          onCreateTournament={tournamentHook.handleCreateTournament}
          onSelectTournament={handleSelectTournament}
          onExportTournament={handleExportTournament}
          onImportTournament={handleImportTournament}
          exportLoading={tournamentExportHook.isExporting}
          importLoading={tournamentExportHook.isImporting}
          exportError={tournamentExportHook.exportError}
          importError={tournamentExportHook.importError}
          exportSuccess={tournamentExportHook.exportSuccess}
          importSuccess={tournamentExportHook.importSuccess}
        />
                    <TournamentDialog
              open={tournamentHook.tournamentDialogOpen}
              onClose={tournamentHook.handleCreateTournamentCancel}
              onConfirm={tournamentHook.handleCreateTournamentConfirm}
              tournamentName={tournamentHook.newTournamentName}
              onTournamentNameChange={tournamentHook.setNewTournamentName}
              tournamentDate={tournamentHook.newTournamentDate}
              onTournamentDateChange={tournamentHook.setNewTournamentDate}
            />
        </>
      );
    }

    if (currentView === 'wrestlers') {
      return <WrestlersView wrestlers={allSubmittedWrestlers} />;
    }

    if (currentView === 'teams') {
      return (
        <TeamsOverview
          wrestlers={allSubmittedWrestlers}
          onRemoveSchool={handleRemoveSchool}
          weightUnit={csvImportHook.weightUnit}
        />
      );
    }

    if (currentView === 'draw') {
      return (
        <DrawView 
          wrestlers={allSubmittedWrestlers} 
          weightUnit={csvImportHook.weightUnit}
          initialDrawState={currentDrawState}
          onDrawStateChange={(drawState: any) => {
            setCurrentDrawState(drawState);
            if (drawState.weightGroups) {
              setCurrentWeightGroups(drawState.weightGroups);
            }
          }}
        />
      );
    }

    return null;
  };

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AuthProvider>
          <Box sx={{ display: 'flex' }}>
            <Header
              drawerOpen={drawerOpen}
              onDrawerToggle={handleDrawerToggle}
              currentTournament={tournamentHook.currentTournament}
              onThemeToggle={handleThemeToggle}
            />

            <Sidebar
              drawerOpen={drawerOpen}
              onDrawerToggle={handleDrawerToggle}
              onMenuItemClick={handleMenuItemClick}
            />

            <Box
              component="main"
              sx={{ 
                flexGrow: 1, 
                p: 3, 
                mt: 8,
                width: { sm: `calc(100% - ${drawerOpen ? 250 : 0}px)` },
                backgroundColor: '#F4F6F8',
                minHeight: '100vh'
              }}
            >
              <Container maxWidth="lg">
                {renderMainContent()}
              </Container>
            </Box>
          </Box>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
